import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import {
	json,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	StatusButton,
	GeneralErrorBoundary,
	ErrorList,
	Field,
	Label,
	Input,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	/* Checkbox, */
} from '#app/ui/components'
import { appName } from '#app/utils/constants.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const SignupSchema = z.object({
	email: EmailSchema,
	marketingConsent: z.boolean().optional().default(false),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: [`email`],
					code: z.ZodIssueCode.custom,
					message: `A user already exists with this email`,
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== `success`) {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === `error` ? 400 : 200 },
		)
	}
	const { email } = submission.value
	const verification = await prepareVerification({
		period: 10 * 60,
		request,
		type: `onboarding`,
		target: email,
	})

	if (verification.rateLimited) {
		return json(
			{
				result: submission.reply({
					formErrors: [
						`A verification code was already sent. Please wait ${verification.retryAfterSeconds} seconds before requesting another.`,
					],
				}),
			},
			{ status: 429 },
		)
	}

	const { verifyUrl, redirectTo, otp } = verification

	const response = await sendEmail({
		to: email,
		subject: `Welcome to ${appName}!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === `success`) {
		return redirect(redirectTo.toString())
	} else {
		return json(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>{`Welcome to ${appName}!`}</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link to get started:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: `Sign Up | ${appName}` }]
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get(`redirectTo`)

	const [form, fields] = useForm({
		id: `signup-form`,
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: `onBlur`,
	})

	return (
		<div className="container flex flex-col justify-center pb-32 pt-20">
			<div className="text-center">
				<h1 className="text-h1">Create Your Account</h1>
				<p className="mt-3 text-body-md text-muted-600">
					Please enter your email.
				</p>
			</div>
			<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
				<Form
					method="POST"
					{...getFormProps(form)}
					className="flex flex-col gap-6"
				>
					<HoneypotInputs />
					<Field>
						<Label htmlFor={fields.email.id}>Email</Label>
						<Input
							{...{
								...getInputProps(fields.email, { type: `email` }),
								autoFocus: true,
								autoComplete: `email`,
							}}
						/>
						<ErrorList errors={fields.email.errors} />
					</Field>

					{/* Data Protection Information */}
					<div className="rounded-md">
						<Dialog>
							<DialogTrigger asChild>
								<button type="button" className="text-sm underline text-left">
									By clicking the “Submit” button you declare that you have read
									and understand the Privacy Policy and the Registration and
									User Account Conditions of this Platform and you consent to
									the processing of your data for the management of your
									registration.
								</button>
							</DialogTrigger>
							<DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
								<DialogHeader>
									<DialogTitle>Data Protection Information</DialogTitle>
								</DialogHeader>
								<DialogDescription asChild>
									<div className="space-y-4 text-sm leading-relaxed text-gray-700">
										<p>
											<strong>Basic Data Protection Information:</strong> the
											data controller is WE LOVE NIGHT S.L. (hereinafter,
											"WELOVENIGHT"). WELOVENIGHT will process Users' data to
											manage the registration and subscription on the Platform
											and to comply with the legal obligations. Users can
											exercise their data protection rights by writing an email
											to{` `}
											<a
												href="mailto:bielmartinez@welovenight.com"
												className="text-blue-600 underline hover:text-blue-800"
											>
												bielmartinez@welovenight.com
											</a>{` `}
											and access information about the processing of your data
											in the{` `}
											<a
												href="/privacy-policy"
												className="text-blue-600 underline hover:text-blue-800"
												target="_blank"
												rel="noopener noreferrer"
											>
												Privacy Policy
											</a>
											.
										</p>
										<p>
											By clicking the "Submit" button you declare that you have
											read and understand the{` `}
											<a
												href="/privacy-policy"
												className="text-blue-600 underline hover:text-blue-800"
												target="_blank"
												rel="noopener noreferrer"
											>
												Privacy Policy
											</a>{` `}
											and the{` `}
											<a
												href="/registration-conditions"
												className="text-blue-600 underline hover:text-blue-800"
												target="_blank"
												rel="noopener noreferrer"
											>
												Registration and User Account Conditions
											</a>{` `}
											of this Platform and you consent to the processing of your
											data for the management of your registration.
										</p>
									</div>
								</DialogDescription>
							</DialogContent>
						</Dialog>

						{/* Marketing Consent Checkbox */}
						{/* <div className="mt-4 flex items-start gap-2">
							<Checkbox
								id="marketingConsent"
								name="marketingConsent"
								className="mt-1"
							/>
							<label
								htmlFor="marketingConsent"
								className="text-sm text-muted-600"
							>
								You wish to receive communications that may be of interest to
								you about WELOVENIGHT and the Event Organisers.
							</label>
						</div> */}
					</div>

					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={isPending ? `pending` : (form.status ?? `idle`)}
						type="submit"
						disabled={isPending}
					>
						Submit
					</StatusButton>
				</Form>
				{/* <ul className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-brand-border py-3">
          {providerNames.map((providerName) => (
            <li key={providerName}>
              <ProviderConnectionForm
                type="Signup"
                providerName={providerName}
                redirectTo={redirectTo}
              />
            </li>
          ))}
        </ul> */}
				<div className="flex items-center justify-center gap-2 pt-6">
					<span className="text-muted-600">Already have an account?</span>
					<Link
						to={
							redirectTo ? `/login?${encodeURIComponent(redirectTo)}` : `/login`
						}
					>
						Log in
					</Link>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
